<template>
  <div>
    <RouterLink
      :to="{
        name: 'ComplianceGroupDomain',
        params: { group: 'eecs', domain: 'fr' },
      }"
      class="btn btn-sm btn-outline-secondary mb-3"
      >← France</RouterLink
    >
    <h1 class="cg-page-title mb-4">Source certificates</h1>

    <div v-if="!short" class="card">
      <div class="card-body text-body-secondary small">
        France has no open shortfall to source. Every monthly portfolio is
        covered or has a request in progress.
      </div>
    </div>

    <div v-else class="d-flex flex-wrap gap-4 align-items-start">
      <div class="cg-main">
        <SectionCard title="Request to Soldera">
          <p class="cg-micro mb-2">Shortfall to source</p>
          <div class="table-responsive mb-4">
            <table class="table table-sm mb-0 align-middle small">
              <thead>
                <tr>
                  <th class="cg-micro">Target portfolio</th>
                  <th class="cg-micro text-end">Consumption</th>
                  <th class="cg-micro text-end">Allocated</th>
                  <th class="cg-micro text-end">To source</th>
                </tr>
              </thead>
              <tbody class="cg-tabular">
                <tr>
                  <td class="fw-semibold">{{ portfolio }}</td>
                  <td class="text-end">{{ fmt(short.consumption) }}</td>
                  <td class="text-end">
                    {{ fmt(short.consumption - short.gap) }}
                  </td>
                  <td class="text-end fw-semibold text-warning-emphasis">
                    {{ fmt(short.gap) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex flex-wrap gap-4">
            <div class="cg-facts">
              <p class="cg-micro mb-2">Product</p>
              <table class="table table-sm mb-0 cg-key-table">
                <tbody>
                  <tr>
                    <td class="text-body-secondary">Certificate type</td>
                    <td>EECS GO</td>
                  </tr>
                  <tr>
                    <td class="text-body-secondary">Vintage</td>
                    <td>{{ short.period }}</td>
                  </tr>
                  <tr>
                    <td class="text-body-secondary">Country</td>
                    <td>AIB Grid-Connected</td>
                  </tr>
                  <tr>
                    <td class="text-body-secondary">Commissioning date</td>
                    <td>Any</td>
                  </tr>
                  <tr>
                    <td class="text-body-secondary">Production support</td>
                    <td>Any</td>
                  </tr>
                  <tr>
                    <td class="text-body-secondary">Technology</td>
                    <td>Hydro / Solar / Wind</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="cg-facts">
              <p class="cg-micro mb-2">Order</p>
              <table class="table table-sm mb-0 cg-key-table">
                <tbody>
                  <tr>
                    <td class="text-body-secondary">Volume</td>
                    <td class="cg-tabular">{{ fmt(short.gap) }} MWh</td>
                  </tr>
                  <tr>
                    <td class="text-body-secondary">Target portfolio</td>
                    <td>{{ portfolio }}</td>
                  </tr>
                  <tr>
                    <td class="text-body-secondary">Purchasing entity</td>
                    <td>Northwind Demo France SAS</td>
                  </tr>
                  <tr>
                    <td class="text-body-secondary">Delivery account</td>
                    <td>Soldera hosted account</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </SectionCard>
      </div>

      <div class="card cg-side shadow-sm">
        <div class="card-body">
          <h2 class="cg-section-title mb-3">Authorize</h2>
          <table class="table table-sm mb-3 cg-key-table">
            <tbody class="cg-tabular">
              <tr>
                <td class="text-body-secondary">Volume to source</td>
                <td>{{ fmt(short.gap) }} MWh</td>
              </tr>
              <tr>
                <td class="text-body-secondary">Request state</td>
                <td>
                  {{
                    submitted
                      ? `1 · ${fmt(short.gap)} MWh`
                      : "Not yet submitted"
                  }}
                </td>
              </tr>
              <tr>
                <td class="text-body-secondary">Approval owner</td>
                <td>A. Thomas</td>
              </tr>
            </tbody>
          </table>
          <template v-if="!submitted">
            <button
              type="button"
              class="btn btn-primary w-100"
              @click="modalOpen = true"
            >
              Submit request
            </button>
            <p class="mb-0 mt-2 text-body-secondary small">
              Once submitted Soldera will return with options within 24 hours.
            </p>
          </template>
          <div v-else class="alert alert-secondary small mb-0">
            <strong>Request pending.</strong> Submitted 10 Sep 2026 · Soldera
            reviewing
          </div>
        </div>
      </div>
    </div>

    <SubmitRequestModal
      v-if="modalOpen && short"
      :volume="short.gap"
      :month="short.period"
      @confirm="authorize"
      @close="modalOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import SectionCard from "./components/SectionCard.vue";
import SubmitRequestModal from "./components/SubmitRequestModal.vue";
import { isGroupKey } from "./fixture/complianceGroupsFixture";
import { franceShortMonth } from "./fixture/derive";
import { fmt } from "./fixture/format";
import { useComplianceDemoStore } from "./useComplianceDemoStore";

const route = useRoute();
const router = useRouter();
const store = useComplianceDemoStore();
// The sourcing request is a French EECS workflow; other group URLs go back to their overview.
watch(
  () => route.params.group,
  (value) => {
    if (value !== "eecs") {
      router.replace({
        name: "ComplianceGroup",
        params: { group: isGroupKey(value) ? value : "eecs" },
      });
    }
  },
  { immediate: true }
);
const short = computed(() => franceShortMonth(store.domains.fr));
const portfolio = computed(() => `France · ${short.value?.period ?? ""}`);
const submitted = computed(() => store.state.requestSubmitted);
const modalOpen = ref(false);

const authorize = () => {
  modalOpen.value = false;
  store.submitRequest();
  router.push({
    name: "ComplianceGroupRequestSubmitted",
    params: { group: "eecs" },
  });
};
</script>

<style scoped lang="scss">
.cg-main {
  flex: 1 1 460px;
  min-width: 0;
}
.cg-facts {
  flex: 1 1 240px;
  min-width: 0;
}
.cg-side {
  position: sticky;
  top: 0;
  flex: 1 1 300px;
  max-width: 340px;
}
</style>
